// Coordinates are CSS viewport pixels, never devicePixelRatio-scaled pixels.
export function clippedSurfaceRect(rect, width, height) {
  if (![rect?.x, rect?.y, rect?.width, rect?.height, width, height].every(Number.isFinite)
      || rect.width <= 0 || rect.height <= 0 || width <= 0 || height <= 0) return null;
  const x = Math.max(0, rect.x), y = Math.max(0, rect.y);
  const right = Math.min(width, rect.x + rect.width);
  const bottom = Math.min(height, rect.y + rect.height);
  return right > x && bottom > y ? { x, y, width: right - x, height: bottom - y } : null;
}

const queues = new WeakMap();
const owners = new WeakMap();
function send(plugin, payload) {
  const next = (queues.get(plugin) || Promise.resolve()).catch(() => {}).then(() => plugin.setMapSurface(payload));
  queues.set(plugin, next);
  return next;
}

export function mountNavigationMapSurface({ element, plugin, lessonEventId, onError, host = window }) {
  if (!plugin?.setMapSurface) {
    onError?.('This build cannot display the native navigation map.');
    return () => {};
  }
  if (owners.has(plugin)) {
    onError?.('Another lesson already owns the navigation map.');
    return () => {};
  }
  const doc = element.ownerDocument;
  const owner = host.crypto.randomUUID();
  owners.set(plugin, owner);
  let closed = false, busy = false, frame = null, lastSent = 0, lastPayload = '';
  const saved = [];
  // Only the map's ancestor chain is cleared; overlay backgrounds stay intact.
  for (let node = element; node; node = node.parentElement) {
    saved.push([node, node.style.getPropertyValue('background'), node.style.getPropertyPriority('background')]);
    node.style.setProperty('background', 'transparent', 'important');
  }
  const restore = () => {
    if (owners.get(plugin) !== owner) return;
    owners.delete(plugin);
    saved.forEach(([node, value, priority]) => {
    if (value) node.style.setProperty('background', value, priority);
    else node.style.removeProperty('background');
    });
  };
  const tick = () => {
    if (closed) return;
    frame = host.requestAnimationFrame(tick);
    if (busy) return;
    const width = host.innerWidth, height = host.innerHeight;
    const viewport = host.visualViewport;
    const rect = clippedSurfaceRect(element.getBoundingClientRect(), width, height);
    const visible = Boolean(element.isConnected && rect && !doc.hidden
      && (!viewport || (viewport.scale === 1 && viewport.offsetLeft === 0 && viewport.offsetTop === 0)));
    const overlays = [];
    // Modal dialogs retain the whole viewport, including their dismiss backdrop.
    const modal = [...doc.querySelectorAll('[role="dialog"], dialog[open], [aria-modal="true"]')]
      .some(node => node.getClientRects().length > 0);
    if (modal) overlays.push({ x: 0, y: 0, width, height });
    else for (const node of doc.querySelectorAll('[data-ndg-map-overlay], button, a, input, select, textarea, [role="button"], [role="menu"]')) {
      if (node.getClientRects().length === 0 || host.getComputedStyle(node).visibility === 'hidden') continue;
      const bounds = clippedSurfaceRect(node.getBoundingClientRect(), width, height);
      if (bounds) overlays.push(bounds);
    }
    // Fail closed if the web surface has more controls than the native bound.
    const payload = { owner, lessonEventId, visible: visible && overlays.length <= 256,
      viewport: { width, height }, rect, overlays: overlays.slice(0, 256) };
    const serialized = JSON.stringify(payload);
    const now = host.performance.now();
    if (serialized === lastPayload && now - lastSent < 400) return;
    lastPayload = serialized; lastSent = now; busy = true;
    send(plugin, payload).catch(error => {
      if (closed) return;
      closed = true;
      host.cancelAnimationFrame(frame);
      restore();
      send(plugin, { owner, lessonEventId, visible: false }).catch(() => {});
      onError?.(error?.message || 'The navigation map could not be displayed.');
    }).finally(() => { busy = false; });
  };
  tick();
  return () => {
    closed = true;
    host.cancelAnimationFrame(frame);
    restore();
    // Shared per-plugin queue prevents an obsolete unmount overtaking a new mount.
    send(plugin, { owner, lessonEventId, visible: false }).catch(() => {});
  };
}
